import { FC, useEffect, useState, useCallback, memo } from 'react';
import {
  Button,
  TextTitle,
  TextBody,
  Spacer,
  Stack,
  Icon,
  AccordionGroup,
  AccordionItem,
  BlockList,
  Input,
  Textarea,
  Divider,
  IconButton,
  Tooltip,
} from '@pypestream/design-system';
import {
  FieldValues,
  SubmitHandler,
  useFormContext,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { TranslationComponent } from '@pypestream/translations';

/* eslint-disable react/prop-types */
const TaskItem: FC<{
  testId?: string;
  field: { id: string };
  index: number;
  isExpanded: boolean;
  onShow: () => void;
  onHide: () => void;
  onRemove: () => void;
}> = memo(({ testId, index, isExpanded, onShow, onHide, onRemove }) => {
  const { control, register, setValue } = useFormContext();

  // Only watch the specific task's name for the header display
  const taskName = useWatch({
    control,
    name: `tasks.${index}.name`,
    defaultValue: '',
  });

  return (
    <AccordionItem
      test-id={testId}
      open={isExpanded}
      onShow={onShow}
      onHide={onHide}
    >
      <Stack
        slot="header"
        justifyContent="space-between"
        alignItems="center"
        gutter="xsmall"
        nowrap
        style={{ flexGrow: 1 }}
      >
        <Stack alignItems="center" gutter="xsmall" nowrap>
          <Icon size="small" name="target" color="purple" />
          <TextBody size="small">{taskName || ''}</TextBody>
        </Stack>
        <IconButton
          name="delete"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        />
      </Stack>
      <BlockList gutter="xsmall">
        <div>
          <TextBody size="small">Task Name</TextBody>
          <Spacer size="2xsmall" />
          <Input
            test-id={`create-solution-modal-task-item-name-input-${index}`}
            variant="outlined"
            placeholder="Order shoes"
            {...register(`tasks.${index}.name`)}
            onInput={(e) => {
              // @ts-expect-error - TODO: fix this
              const { value } = e.target;
              setValue(`tasks.${index}.name`, value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />
        </div>
        <div>
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Example Phrases*</TextBody>
            <Tooltip
              placement="right"
              content="Write at least one natural way someone might ask for this. The Supervisor Agent uses these Example Phrases to understand what the user wants and route them to the right workflow."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <Spacer size="2xsmall" />
          <Textarea
            variant="outlined"
            placeholder="I want to buy shoes | Help me order sneakers"
            {...register(`tasks.${index}.description`)}
          />
        </div>
      </BlockList>
    </AccordionItem>
  );
});

TaskItem.displayName = 'TaskItem';

export const StepTasks: FC<{
  backStep?: () => void;
  onSubmit: SubmitHandler<FieldValues>;
  disabled?: boolean;
  onPopoverStateChange?: (isOpen: boolean) => void;
}> = ({ backStep, onSubmit, disabled = false }) => {
  const { handleSubmit, control, setValue, getValues } = useFormContext();
  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<number>>(
    new Set()
  );

  const {
    fields,
    append,
    remove: removeTask,
  } = useFieldArray({
    control,
    name: 'tasks',
  });

  useEffect(() => {
    const tasks = getValues('tasks');
    if (!tasks || tasks.length === 0) {
      setValue('tasks', []);
    }
  }, [setValue, getValues, fields.length]);

  const addNewTask = useCallback(() => {
    const newTaskId = `task-${Date.now()}`;
    append({
      id: newTaskId,
      name: '',
      description: '',
    });
    setExpandedTaskIds((prev) => new Set(prev).add(fields.length));
  }, [append, fields.length]);

  const handleShow = useCallback((index: number) => {
    setExpandedTaskIds((prev) => new Set(prev).add(index));
  }, []);

  const handleHide = useCallback((index: number) => {
    setExpandedTaskIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      removeTask(index);
      setExpandedTaskIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    },
    [removeTask]
  );

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <TextTitle size="small" textAlign="center">
          Define Tasks
        </TextTitle>
        <Spacer size="small" />
        <TextBody variant="secondary" textAlign="center">
          <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.tasks_subtitle">
            List the key tasks you want your solution to accomplish. The
            Supervisor Agent will use these to understand user requests and
            route them to the correct workflows.
          </TranslationComponent>
        </TextBody>
        <Spacer size="xlarge" />
        <Spacer />
        <AccordionGroup multiple>
          {fields.map((field, index) => (
            <TaskItem
              testId={`create-solution-modal-task-item-${index}`}
              key={field.id}
              field={field}
              index={index}
              isExpanded={expandedTaskIds.has(index)}
              onShow={() => handleShow(index)}
              onHide={() => handleHide(index)}
              onRemove={() => handleRemove(index)}
            />
          ))}
        </AccordionGroup>
        <Divider />
        <Spacer size="xsmall" />
        <Button
          test-id="create-solution-modal-add-task-btn"
          size="large"
          variant="ghost"
          onClick={addNewTask}
        >
          <Icon
            name="plus"
            size="small"
            color="purple"
            variant="filled"
            slot="prefix"
          />
          Add task
        </Button>

        <Spacer size="xlarge" />
        <Stack justifyContent="space-between">
          <Button
            size="large"
            variant="ghost"
            onClick={backStep}
            disabled={disabled}
          >
            Back
          </Button>

          <Button
            test-id="create-solution-step-tasks-next-btn"
            size="large"
            onClick={handleSubmit(onSubmit)}
            disabled={disabled}
          >
            Done
          </Button>
        </Stack>
      </form>
    </>
  );
};
